import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, DollarSign, Clock, AlertTriangle, Lightbulb, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppContext } from "@/lib/onboardingContext";
import { budgetRanges } from "@/lib/businessData";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";

interface ForecastInputs {
  avgTicket: number;
  customersPerDay: number;
  monthlyRent: number;
  cogsPercent: number;
  laborPercent: number;
  otherMonthlyExpenses: number;
}

function computeForecast(inputs: ForecastInputs, startupBudget: number, daysPerWeek: number) {
  const { avgTicket, customersPerDay, monthlyRent, cogsPercent, laborPercent, otherMonthlyExpenses } = inputs;
  const daysPerMonth = (daysPerWeek / 7) * 30;

  const scenarios = {
    conservative: { revMul: 0.7, costMul: 1.15, rampMonths: 4, label: "Conservative" },
    realistic: { revMul: 1.0, costMul: 1.0, rampMonths: 3, label: "Realistic" },
    optimistic: { revMul: 1.3, costMul: 0.9, rampMonths: 2, label: "Optimistic" },
  };

  const results: Record<string, any> = {};

  for (const [key, s] of Object.entries(scenarios)) {
    const months = [];
    let cumCash = -startupBudget;
    let breakEvenMonth = -1;
    let cashFlowPositiveMonth = -1;

    for (let m = 1; m <= 12; m++) {
      const rampFactor = m <= s.rampMonths ? 0.5 + (0.5 * m / s.rampMonths) : 1;
      const revenue = Math.round(avgTicket * customersPerDay * daysPerMonth * s.revMul * rampFactor);
      const cogs = Math.round(revenue * (cogsPercent / 100));
      const labor = Math.round(revenue * (laborPercent / 100));
      const rent = monthlyRent;
      const other = Math.round(otherMonthlyExpenses * s.costMul);
      const totalExpenses = cogs + labor + rent + other;
      const profit = revenue - totalExpenses;
      cumCash += profit;

      if (cashFlowPositiveMonth === -1 && profit > 0) cashFlowPositiveMonth = m;
      if (breakEvenMonth === -1 && cumCash >= 0) breakEvenMonth = m;

      months.push({ month: m, label: `M${m}`, revenue, cogs, labor, rent, other, totalExpenses, profit, cumCash });
    }

    const steadyRevenue = Math.round(avgTicket * customersPerDay * daysPerMonth * s.revMul);
    const steadyExpenses = Math.round(steadyRevenue * (cogsPercent / 100)) + Math.round(steadyRevenue * (laborPercent / 100)) + monthlyRent + Math.round(otherMonthlyExpenses * s.costMul);
    const steadyProfit = steadyRevenue - steadyExpenses;
    const monthlyBurn = Math.max(0, steadyExpenses - steadyRevenue);
    const runway = monthlyBurn > 0 ? Math.round(startupBudget / monthlyBurn) : 99;

    const fullRoiMonths = steadyProfit > 0 ? Math.ceil(startupBudget / steadyProfit) : -1;

    results[key] = {
      ...s,
      months,
      cashFlowPositiveMonth: cashFlowPositiveMonth > 0 ? cashFlowPositiveMonth : ">12",
      fullRoiMonths: fullRoiMonths > 0 ? fullRoiMonths : "N/A",
      breakEvenMonth: breakEvenMonth > 0 ? breakEvenMonth : ">12",
      steadyRevenue,
      steadyExpenses,
      steadyProfit,
      runway: Math.min(runway, 99),
      annualRevenue: months.reduce((s, m) => s + m.revenue, 0),
      annualProfit: months.reduce((s, m) => s + m.profit, 0),
    };
  }

  return results;
}

export default function Forecast() {
  const navigate = useNavigate();
  const { results: appResults, onboarding, selectedNeighborhood } = useAppContext();
  const city = onboarding?.city || "miami";
  const cs = getCurrencySymbol(city);
  const cityKey = city.toLowerCase().trim();
  const activeNeighborhood = selectedNeighborhood?.cityKey === cityKey ? selectedNeighborhood : null;
  const budgetValue = budgetRanges.find(b => b.id === appResults?.budgetLabel?.toLowerCase()?.replace(/[^a-z0-9]/g, "-"))?.value
    || appResults?.totalEstimate || 100000;

  // Room-driven venues (Hotel & B&B): revenue = rooms × ADR × occupancy × 30.
  // We seed the seat-based forecast engine so customersPerDay = total occupied
  // room-nights/day and avgTicket = weighted avg ADR. Multiplied across a
  // 30-day month (daysPerWeek=7) this yields the canonical room-night formula.
  const isRoomDriven =
    onboarding?.businessType === "hotel" ||
    onboarding?.businessType === "bed-and-breakfast";
  const guestRooms = onboarding?.guestRooms ?? [];
  const totalRooms = guestRooms.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);

  // Total seating capacity drives expected daily customers (seat-driven path).
  // Industry rule of thumb: customers/day ≈ seats × turns/day.
  const totalCapacity =
    (Number(onboarding?.indoorSeats) || 0) +
    (Number(onboarding?.outdoorSeats) || 0) +
    (Number(onboarding?.barSeats) || 0);
  const turnsPerDay = 2.5; // average across day-parts for a typical food/bev venue

  // Compute weighted ADR & occupancy across all room types.
  let weightedAdrLocal = 0;
  let occupiedRoomNightsPerDay = 0;
  if (isRoomDriven && totalRooms > 0) {
    let revenuePerNight = 0;
    for (const r of guestRooms) {
      const qty = Number(r.quantity) || 0;
      const adr = Number(r.adr) || 0; // already in local currency
      const occ = (Number(r.occupancy) || 0) / 100;
      revenuePerNight += qty * adr * occ;
      occupiedRoomNightsPerDay += qty * occ;
    }
    // Weighted avg ADR across occupied rooms (avoids divide-by-zero).
    weightedAdrLocal = occupiedRoomNightsPerDay > 0
      ? revenuePerNight / occupiedRoomNightsPerDay
      : 0;
  }

  const seededCustomers = isRoomDriven && occupiedRoomNightsPerDay > 0
    ? Math.max(1, Math.round(occupiedRoomNightsPerDay))
    : totalCapacity > 0
      ? Math.max(20, Math.round(totalCapacity * turnsPerDay))
      : 60;

  const seededAvgTicket = isRoomDriven && weightedAdrLocal > 0
    ? Math.round(weightedAdrLocal)
    : convertFromUSD(18, city);

  // Default monthly rent: neighborhood midpoint if selected, otherwise city average.
  const defaultMonthlyRent = activeNeighborhood?.monthlyRentLocal ?? convertFromUSD(3500, city);

  // Convert default USD values to local currency for initial inputs
  const [inputs, setInputs] = useState<ForecastInputs>({
    avgTicket: seededAvgTicket,
    customersPerDay: seededCustomers,
    monthlyRent: defaultMonthlyRent,
    cogsPercent: isRoomDriven ? 12 : 30, // hotels: lower variable cost per room-night
    laborPercent: isRoomDriven ? 32 : 28,
    otherMonthlyExpenses: convertFromUSD(2500, city),
  });

  // Keep monthly rent in sync when the user picks/changes a neighborhood
  // selection from the Locations page (only overwrite the rent field).
  useEffect(() => {
    setInputs(prev => ({ ...prev, monthlyRent: defaultMonthlyRent }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNeighborhood?.name, activeNeighborhood?.monthlyRentLocal]);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [activeScenario, setActiveScenario] = useState<"conservative" | "realistic" | "optimistic">("realistic");

  const daysPerWeek = appResults?.openingSummary?.daysPerWeek || 7;
  const startupBudget = appResults?.totalEstimate ? convertFromUSD(appResults.totalEstimate, city) : convertFromUSD(budgetValue, city);

  const forecast = useMemo(() => computeForecast(inputs, startupBudget, daysPerWeek), [inputs, startupBudget, daysPerWeek]);

  const upd = (field: keyof ForecastInputs, val: string) => {
    setInputs(prev => ({ ...prev, [field]: parseFloat(val) || 0 }));
  };

  const active = forecast[activeScenario];
  const scenarioColors = { conservative: "text-amber-600", realistic: "text-primary", optimistic: "text-emerald-600" };

  const fmtVal = (v: number) => `${cs}${v.toLocaleString()}`;
  const fmtK = (v: number) => `${cs}${(v / 1000).toFixed(0)}K`;

  const chartData = active.months.map((m: any) => ({
    name: m.label,
    Revenue: m.revenue,
    Expenses: m.totalExpenses,
    Profit: m.profit,
  }));

  const comparisonData = Array.from({ length: 12 }, (_, i) => ({
    name: `M${i + 1}`,
    Conservative: forecast.conservative.months[i].profit,
    Realistic: forecast.realistic.months[i].profit,
    Optimistic: forecast.optimistic.months[i].profit,
  }));

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}><ArrowLeft className="w-5 h-5" /></Button>
            <span className="font-heading text-xl font-medium tracking-tight text-foreground">
              oplif<span className="text-primary">.</span>
            </span>
          </div>
          <span className="text-sm text-muted-foreground">Financial Forecast</span>
        </div>
      </div>

      <div className="container pt-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl font-medium text-foreground">Financial Forecast</h1>
          <p className="mt-2 text-muted-foreground">Estimate your revenue, costs, and break-even — before you commit to anything.</p>
        </motion.div>

        {/* Disclaimer */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="mt-4 mb-8 p-3 rounded-lg bg-accent/5 border border-accent/20 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-accent mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">This is an <span className="font-medium text-foreground">estimated planning tool</span>, not guaranteed financial advice. Actual results will vary based on location, execution, and market conditions.</p>
        </motion.div>

        {/* Neighborhood-applied rent banner */}
        {activeNeighborhood && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-lg border-2 border-primary/30 bg-primary/5 flex items-start gap-2">
            <TrendingUp className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-foreground">
              <span className="font-medium">Rent updated based on {activeNeighborhood.name}</span> — adjust manually if needed.
              <span className="text-muted-foreground"> ~{cs}{activeNeighborhood.monthlyRentLocal.toLocaleString()}/mo applied to Monthly Rent.</span>
            </p>
          </motion.div>
        )}

        {/* Inputs with explanations */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-card rounded-xl p-6 border border-border shadow-card mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-medium text-foreground">Your Assumptions</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { label: `Average Ticket (${cs})`, field: "avgTicket" as const, placeholder: String(convertFromUSD(18, city)), explain: `How much does one customer spend on average per visit? For a café it might be ${cs}${convertFromUSD(6, city)}–${cs}${convertFromUSD(10, city)}, for a restaurant ${cs}${convertFromUSD(15, city)}–${cs}${convertFromUSD(30, city)}.` },
              { label: "Customers Per Day", field: "customersPerDay" as const, placeholder: String(seededCustomers), explain: totalCapacity > 0 ? `Pre-filled from your seating: ${totalCapacity} seats × ~${turnsPerDay} turns/day. Adjust based on your service style.` : "How many paying customers do you expect per day? A small café might see 50–100, a restaurant 40–80." },
              { label: `Monthly Rent (${cs})`, field: "monthlyRent" as const, placeholder: String(convertFromUSD(3500, city)), explain: "How much will you pay each month to your landlord for the space? This is usually your biggest fixed cost." },
              { label: "COGS (%)", field: "cogsPercent" as const, placeholder: "30", explain: "Cost of Goods Sold — the percentage of revenue spent on ingredients and raw materials. For restaurants, this is usually 25–35%. A bar might be 18–25%." },
              { label: "Labor Cost (%)", field: "laborPercent" as const, placeholder: "28", explain: "The percentage of revenue that goes toward paying your staff (wages, tips, payroll taxes). For most food businesses, 25–35% is typical." },
              { label: `Other Monthly Expenses (${cs})`, field: "otherMonthlyExpenses" as const, placeholder: String(convertFromUSD(2500, city)), explain: "Everything else: utilities, insurance, cleaning supplies, marketing, software subscriptions, maintenance, and unexpected costs." },
            ].map(inp => (
              <div key={inp.field} className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">{inp.label}</label>
                <Input type="number" className="h-10" placeholder={inp.placeholder} value={inputs[inp.field] || ""} onChange={e => upd(inp.field, e.target.value)} />
                <p className="text-[11px] text-muted-foreground leading-snug">{inp.explain}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            Startup budget: <span className="font-medium text-foreground">{fmtVal(startupBudget)}</span> · {daysPerWeek} days/week
          </div>
        </motion.div>

        {/* Scenario Tabs */}
        <div className="flex gap-2 mb-6">
          {(["conservative", "realistic", "optimistic"] as const).map(s => (
            <button key={s} onClick={() => setActiveScenario(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeScenario === s ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:border-primary/40"}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          {[
            { label: "Monthly Revenue", value: fmtVal(active.steadyRevenue), icon: TrendingUp, sub: "at full capacity" },
            { label: "Monthly Profit", value: fmtVal(active.steadyProfit), icon: DollarSign, sub: active.steadyProfit >= 0 ? "profitable" : "operating at a loss", color: active.steadyProfit >= 0 ? "text-primary" : "text-destructive" },
            { label: "Cash Flow Positive", value: typeof active.cashFlowPositiveMonth === "number" ? `Month ${active.cashFlowPositiveMonth}` : "12+ months", icon: TrendingUp, sub: "revenue covers monthly expenses", color: "text-primary" },
            { label: "Full ROI Timeline", value: typeof active.fullRoiMonths === "number" ? `${active.fullRoiMonths} months` : "N/A", icon: Clock, sub: `${fmtVal(startupBudget)} ÷ ${active.steadyProfit > 0 ? fmtVal(active.steadyProfit) : `${cs}0`}/mo`, color: typeof active.fullRoiMonths === "number" && active.fullRoiMonths <= 24 ? "text-primary" : "text-amber-600" },
          ].map((card, i) => (
            <motion.div key={card.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05 }}
              className="bg-card rounded-xl p-4 border border-border shadow-card">
              <card.icon className="w-5 h-5 text-muted-foreground mb-2" />
              <div className={`font-heading text-xl font-medium ${card.color || "text-foreground"}`}>{card.value}</div>
              <div className="text-xs text-muted-foreground">{card.label}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{card.sub}</div>
            </motion.div>
          ))}
        </div>

        {/* Explanation */}
        <div className="mb-8 px-1">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Cash Flow Positive</span> means your monthly revenue covers all monthly expenses. <span className="font-medium text-foreground">Full ROI</span> is when your total startup investment is fully recovered from accumulated profits.
          </p>
        </div>

        {/* Revenue vs Expenses Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-card rounded-xl p-6 border border-border shadow-card mb-8">
          <h3 className="font-heading font-medium text-foreground mb-4">Revenue vs Expenses (12 months)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => fmtK(v)} />
                <Tooltip formatter={(v: number) => fmtVal(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Legend />
                <Bar dataKey="Revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill="hsl(var(--muted))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Scenario Comparison Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-card rounded-xl p-6 border border-border shadow-card mb-8">
          <h3 className="font-heading font-medium text-foreground mb-4">Profit Comparison — All Scenarios</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => fmtK(v)} />
                <Tooltip formatter={(v: number) => fmtVal(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Legend />
                <Line type="monotone" dataKey="Conservative" stroke="#d97706" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Realistic" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Optimistic" stroke="#059669" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Monthly Breakdown Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-card rounded-xl p-6 border border-border shadow-card mb-8 overflow-x-auto">
          <h3 className="font-heading font-medium text-foreground mb-4">Monthly Breakdown — {active.label}</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground border-b border-border">
                <th className="text-left py-2 pr-4">Month</th>
                <th className="text-right py-2 px-2">Revenue</th>
                <th className="text-right py-2 px-2">COGS</th>
                <th className="text-right py-2 px-2">Labor</th>
                <th className="text-right py-2 px-2">Rent</th>
                <th className="text-right py-2 px-2">Other</th>
                <th className="text-right py-2 px-2 font-medium">Profit</th>
                <th className="text-right py-2 pl-2">Cum. Cash</th>
              </tr>
            </thead>
            <tbody>
              {active.months.map((m: any) => (
                <tr key={m.month} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-medium text-foreground">{m.label}</td>
                  <td className="text-right py-2 px-2 text-foreground">{fmtVal(m.revenue)}</td>
                  <td className="text-right py-2 px-2 text-muted-foreground">{fmtVal(m.cogs)}</td>
                  <td className="text-right py-2 px-2 text-muted-foreground">{fmtVal(m.labor)}</td>
                  <td className="text-right py-2 px-2 text-muted-foreground">{fmtVal(m.rent)}</td>
                  <td className="text-right py-2 px-2 text-muted-foreground">{fmtVal(m.other)}</td>
                  <td className={`text-right py-2 px-2 font-medium ${m.profit >= 0 ? "text-primary" : "text-destructive"}`}>{fmtVal(m.profit)}</td>
                  <td className={`text-right py-2 pl-2 ${m.cumCash >= 0 ? "text-primary" : "text-destructive"}`}>{fmtVal(m.cumCash)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Scenario Summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          className="bg-card rounded-xl p-6 border border-border shadow-card mb-8">
          <h3 className="font-heading font-medium text-foreground mb-4">Scenario Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(["conservative", "realistic", "optimistic"] as const).map(key => {
              const s = forecast[key];
              return (
                <div key={key} className={`p-4 rounded-xl border ${activeScenario === key ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className={`text-sm font-medium mb-2 ${scenarioColors[key]}`}>{s.label}</div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Annual revenue</span><span className="font-medium text-foreground">{fmtVal(s.annualRevenue)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Annual profit</span><span className={`font-medium ${s.annualProfit >= 0 ? "text-primary" : "text-destructive"}`}>{fmtVal(s.annualProfit)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Cash flow positive</span><span className="font-medium text-foreground">{typeof s.cashFlowPositiveMonth === "number" ? `Month ${s.cashFlowPositiveMonth}` : "12+"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Full ROI</span><span className="font-medium text-foreground">{typeof s.fullRoiMonths === "number" ? `${s.fullRoiMonths} mo` : "N/A"}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Assumptions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <button onClick={() => setShowAssumptions(!showAssumptions)}
            className="w-full bg-card rounded-xl p-4 border border-border shadow-card flex items-center justify-between text-sm font-medium text-foreground">
            <span>Assumptions & Methodology</span>
            {showAssumptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showAssumptions && (
            <div className="bg-card rounded-b-xl p-6 border border-t-0 border-border text-sm text-muted-foreground space-y-4">
              <div>
                <p className="font-medium text-foreground mb-1">📊 How is revenue calculated?</p>
                <p>Revenue = average ticket × customers per day × days you're open per month. For example, if your average ticket is {cs}{convertFromUSD(18, city)}, you serve 60 customers/day, and you're open 26 days/month, your monthly revenue would be {fmtVal(convertFromUSD(18, city) * 60 * 26)}.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">📉 What are the 3 scenarios?</p>
                <p><span className="text-amber-600 font-medium">Conservative</span> — assumes only 70% of your expected customers come, and your costs are 15% higher than planned. It takes 4 months to reach full capacity. This is the "things go wrong" scenario.</p>
                <p className="mt-1"><span className="text-primary font-medium">Realistic</span> — uses your exact inputs as-is. Assumes a 3-month ramp-up period where you start at 50% of full traffic and grow steadily.</p>
                <p className="mt-1"><span className="text-emerald-600 font-medium">Optimistic</span> — assumes 30% more customers than expected and 10% lower costs. You ramp up to full capacity in just 2 months.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">🚀 What is the ramp-up period?</p>
                <p>When you first open, you won't have full customer traffic from day one. The ramp-up models how your business gradually builds up to full capacity. Month 1 starts at about 50% of your target, and increases each month until you hit 100%.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">💰 What is COGS?</p>
                <p>COGS means "Cost of Goods Sold" — it's the cost of the ingredients and materials you use to make what you sell. If you sell a {cs}{convertFromUSD(10, city)} sandwich and the ingredients cost {cs}{convertFromUSD(3, city)}, your COGS is 30%. For restaurants, 28–35% is typical. For bars, 18–25%.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">⏱️ What is break-even?</p>
                <p>Break-even is the month when your total earnings finally exceed your total spending (including your startup costs). Before break-even, you're still recovering your initial investment. After break-even, you're actually making money.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">🏦 What is cash runway?</p>
                <p>Cash runway tells you how many months you can keep operating before running out of money, assuming zero revenue. It's calculated as: startup budget ÷ monthly expenses. If your runway is 6 months, you have 6 months of breathing room even if no customers show up.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">📋 What are "Other Monthly Expenses"?</p>
                <p>This covers everything besides food costs, labor, and rent: utilities (electricity, gas, water), insurance, cleaning supplies, credit card processing fees, marketing, software (POS, accounting), maintenance, and a small buffer for unexpected costs.</p>
              </div>
              <p className="text-xs italic pt-2 border-t border-border">These are estimates for planning purposes only. Consult a financial advisor before making investment decisions.</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
